import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { createCheckout, verifyPayment } from "../lib/masterApi";
import { openRazorpay } from "../lib/razorpay";
import { buildRedirectUrl, getReturnUrl, getToken, setToken } from "../lib/planSession";
import { extractApiErrorMessage } from "../lib/api";

// Orchestrates "Choose Plan": auth (modal if needed) -> Razorpay order
// -> payment modal -> server verification -> redirect to the panel.
export default function usePlanPurchase() {
  const [busyPlanId, setBusyPlanId] = useState(null);
  const [authPlan, setAuthPlan] = useState(null);
  const busy = useRef(false);

  const checkout = useCallback(async (plan, token) => {
    if (busy.current) return;
    busy.current = true;
    setBusyPlanId(plan.id);
    // A returnUrl means the user came from a panel; otherwise it's a fresh
    // guest and we send them to the default panel with just their token.
    const returnUrl = getReturnUrl();
    try {
      const order = await createCheckout({ planId: plan.id, billingInterval: plan.billingInterval, token });
      if (!order.isFree) {
        const isSubscription = Boolean(order.isSubscription || order.subscriptionId);
        const paid = await openRazorpay({
          key: order.keyId,
          ...(isSubscription
            ? { subscription_id: order.subscriptionId }
            : {
                order_id: order.orderId,
                amount: order.amount,
                currency: order.currency || "INR",
              }),
          name: "MagicBolt AI",
          description: order.plan?.name || plan.name,
          prefill: order.prefill || undefined,
          theme: { color: "#FF6B00" },
        });
        await verifyPayment({
          razorpayOrderId: paid.razorpay_order_id,
          razorpaySubscriptionId: paid.razorpay_subscription_id,
          razorpayPaymentId: paid.razorpay_payment_id,
          razorpaySignature: paid.razorpay_signature,
          planId: plan.id,
          token,
        });
      }
      toast.success(
        order.allowTrial
          ? "Free Trial activated! Redirecting to your workspace…"
          : `${plan.name} activated. Redirecting to your workspace…`
      );
      window.location.assign(buildRedirectUrl({ token, returnUrl, subscribed: true }));
    } catch (err) {
      if (!err?.dismissed) {
        toast.error(err?.message && !err?.response ? err.message : extractApiErrorMessage(err));
      }
      busy.current = false;
      setBusyPlanId(null);
    }
  }, []);

  const purchase = useCallback(
    (plan) => {
      const token = getToken();
      if (!token) return setAuthPlan(plan);
      return checkout(plan, token);
    },
    [checkout]
  );

  const onAuthenticated = useCallback(
    (token) => {
      setToken(token);
      const plan = authPlan;
      setAuthPlan(null);
      if (plan) checkout(plan, token);
    },
    [authPlan, checkout]
  );

  return { purchase, busyPlanId, authPlan, closeAuth: () => setAuthPlan(null), onAuthenticated };
}
